/*
 * Interactive CV filtering.
 *
 * Every category starts switched on and every role visible. Clicking a tag
 * switches that category off; a role stays visible while it carries at least
 * one category still on, and disappears only once everything it is tagged
 * with has been switched off. The model is "remove what you are not
 * interested in" — the highlighted buttons always spell out what
 * "Show experience relevant to:" currently means.
 *
 * Everything is rendered up front and hidden client-side, so with JavaScript
 * off the CV reads in full — which is exactly the all-on default.
 */

export function initCvFilters() {
  const root = document.querySelector("[data-cv]");
  if (!root) return;

  const buttons = Array.from(root.querySelectorAll("[data-cv-filter]"));
  const roles = Array.from(root.querySelectorAll("[data-cv-tags]"));
  const status = root.querySelector("[data-cv-status]");
  const empty = root.querySelector("[data-cv-empty]");
  const reset = root.querySelector("[data-cv-reset]");
  if (!buttons.length || !roles.length) return;

  const allTags = buttons.map((b) => b.dataset.cvFilter);
  const selected = new Set(allTags);

  function tagsOf(role) {
    return (role.dataset.cvTags || "").split(/\s+/).filter(Boolean);
  }

  function apply() {
    let visible = 0;

    roles.forEach((role) => {
      const matches = tagsOf(role).some((tag) => selected.has(tag));
      role.hidden = !matches;
      if (matches) visible += 1;
    });

    if (empty) empty.hidden = visible !== 0;
    if (reset) reset.hidden = selected.size === allTags.length;

    if (status) {
      if (selected.size === allTags.length) {
        status.textContent = `Showing all ${roles.length} roles.`;
      } else if (selected.size === 0) {
        status.textContent =
          `Showing 0 of ${roles.length} roles — no categories selected.`;
      } else {
        const names = buttons
          .filter((b) => selected.has(b.dataset.cvFilter))
          .map((b) => b.textContent.trim());
        status.textContent =
          `Showing ${visible} of ${roles.length} roles relevant to ` +
          `${names.join(" or ")}.`;
      }
    }

    syncUrl();
  }

  /* The URL carries the on-set, so a link can be sent with a view already
     narrowed. No parameter means the default all-on view; a present but
     empty one round-trips the all-off state. */
  function syncUrl() {
    const url = new URL(window.location.href);
    if (selected.size === allTags.length) {
      url.searchParams.delete("filter");
    } else {
      url.searchParams.set("filter", Array.from(selected).join(","));
    }
    window.history.replaceState({}, "", url);
  }

  function toggle(button) {
    const tag = button.dataset.cvFilter;
    const nowOn = !selected.has(tag);

    if (nowOn) selected.add(tag);
    else selected.delete(tag);

    button.setAttribute("aria-pressed", String(nowOn));
    apply();
  }

  buttons.forEach((button) => {
    button.addEventListener("click", () => toggle(button));
  });

  if (reset) {
    reset.addEventListener("click", () => {
      allTags.forEach((tag) => selected.add(tag));
      buttons.forEach((b) => b.setAttribute("aria-pressed", "true"));
      apply();
    });
  }

  // A filter parameter names the on-set; without one everything stays on.
  const params = new URL(window.location.href).searchParams;
  if (params.has("filter")) {
    const named = new Set(params.get("filter").split(",").filter(Boolean));
    selected.clear();
    buttons.forEach((b) => {
      const on = named.has(b.dataset.cvFilter);
      if (on) selected.add(b.dataset.cvFilter);
      b.setAttribute("aria-pressed", String(on));
    });
  }

  apply();
}
